import type {
  ReorderQueueRequestsBody,
  ReorderQueueRequestsResponse,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import {
  projectRequestsKeys,
  projectRequestStatisticsKeys,
  requestListKeys,
} from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseReorderQueueRequestsOptions {
  onError?: (error: Error) => void;
  onSuccess?: (data: ReorderQueueRequestsResponse) => void;
}

export function useReorderQueueRequests(
  options: UseReorderQueueRequestsOptions = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedRequestIds: number[]) => {
      const body: ReorderQueueRequestsBody = {
        orderedRequestIds,
      };

      return await apiClient
        .url("/requests/list/queue/reorder")
        .post(body)
        .json<ReorderQueueRequestsResponse>();
    },
    onError: (error: Error) => {
      toast.add({
        description: "Failed to reorder queue. Please try again.",
        timeout: 3000,
        title: "Error",
        type: "error",
      });
      options.onError?.(error);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: requestListKeys.all,
        refetchType: "all",
      });

      await queryClient.invalidateQueries({
        queryKey: projectRequestsKeys.all,
        refetchType: "all",
      });

      await queryClient.invalidateQueries({
        queryKey: projectRequestStatisticsKeys.all,
      });

      options.onSuccess?.(data);
    },
  });
}
